/**
 * Feature flags registry for enabling or disabling specific features in the app.
 *
 * Purpose
 * - Centralizes all feature flags in a single, typed registry.
 * - Ensures flags are discoverable and documented in one place.
 * - Prevents ad-hoc booleans scattered across the codebase.
 *
 * Override support
 * - NEXT_PUBLIC_FEATURE_FLAGS can be JSON `("{\"FLAG\":true}")`
 * - or a comma list: `"FLAG_A,FLAG_B,!FLAG_C,FLAG_D=false"`
 *
 * Parsing rules for comma lists
 * - FLAG_A               -> true
 * - !FLAG_A or -FLAG_A   -> false
 * - FLAG_A=true          -> true
 * - FLAG_A=false         -> false
 *
 * Notes
 * - Environment overrides are optional and only apply at build/runtime.
 * - Flags not defined in the registry are ignored.
 */
export const FEATURE_FLAG_DEFINITIONS = {
  // ==================================================
  // Manager Features
  // ==================================================

  /**
   * Controls access to the manager dashboard and its primary entry point.
   * If disabled, manager routes and navigation items should be hidden.
   */
  ENABLE_MANAGER_DASHBOARD: {
    defaultValue: true,
    description: `Enable the Manager Dashboard functionality.`,
  },

  /**
   * Enables manager analytics modules and analytics-specific navigation.
   * Intended to allow phased rollout of analytics in the manager area.
   */
  ENABLE_MANAGER_ANALYTICS: {
    defaultValue: false,
    description: `Enable the Manager Analytics functionality.`,
  },

  /**
   * When true, the app uses manager-specific API endpoints with richer data.
   * When false, the app falls back to generic endpoints.
   */
  ENABLE_MANAGER_API_ENDPOINTS: {
    defaultValue: true,
    description: `Enable manager-specific API endpoints for manager dashboard and related features.`,
  },

  // ==================================================
  // Certification Features
  // ==================================================

  /**
   * Enables certification locking to prevent post-certification changes.
   * Intended for compliance or audit-ready certification programs.
   */
  ENABLE_CERTIFICATION_LOCKING: {
    defaultValue: false,
    description: `Enable certification locking to freeze a user's progress after certification.`,
  },

  /**
   * Enables the final certification step inside a certification program.
   * Intended for compliance or audit-ready certification programs.
   */
  ENABLE_FINAL_CERTIFICATION: {
    defaultValue: false,
    description: `Enable final certification step inside a certification program.`,
  },

  // ==================================================
  // Sales Rep Features
  // ==================================================

  /**
   * Enables the Sales Rep dashboard and related route/navigation entry.
   * Use this to pause or gate sales-rep access at a feature level.
   */
  ENABLE_SALES_REP_DASHBOARD: {
    defaultValue: true,
    description: `Enable the Sales Rep Dashboard functionality.`,
  },

  /**
   * Enables the Sales Rep certifications listing/landing page.
   * Useful for staged rollouts of certification experiences.
   */
  ENABLE_SALES_REP_CERTIFICATION_PAGE: {
    defaultValue: false,
    description: `Enable the Sales Rep Certification page functionality.`,
  },

  /**
   * Enables the certificate detail page for Sales Rep certifications.
   * Intended to be used with the certifications listing page.
   */
  ENABLE_SALES_REP_CERTIFICATION_CERTIFICATE_PAGE: {
    defaultValue: true,
    description: `Enable the Sales Rep Certification Certificate page functionality.`,
  },

  /**
   * Enables the Sales Rep score page for simulations.
   * When false, users should not see score routes or related CTAs.
   */
  ENABLE_SALES_REP_SCORE_PAGE: {
    defaultValue: true,
    description: `Enable the Simulation Score page functionality.`,
  },

  /**
   * Enables the score history section on the Sales Rep score page.
   * Useful for toggling historical data visibility independently.
   */
  ENABLE_SALES_REP_SCORE_PAGE_HISTORY: {
    defaultValue: true,
    description: `Enable the Simulation Score page's history functionality.`,
  },

  // ==================================================
  // General Features
  // ==================================================

  /**
   * Enables the Share with Manager button and associated workflow.
   * When disabled, share CTAs should be hidden.
   */
  ENABLE_SHARE_WITH_MANAGER: {
    defaultValue: true,
    description: `Enable the "Share with Manager" button functionality.`,
  },

  /**
   * Enables Graph API profile image fetching for managed users.
   * When disabled, fall back to initials or placeholder imagery.
   */
  ENABLE_MANAGED_USER_PROFILE_IMAGE: {
    defaultValue: true,
    description: `Enable fetching and displaying user profile images from Microsoft Graph API.`,
  },

  /**
   * Enables in-app notifications for user actions and system events.
   * When enabled, users will receive notifications for important events.
   */
  ENABLE_APP_NOTIFICATIONS: {
    defaultValue: false,
    description: `Enable in-app notifications for user actions and system events.`,
  },

  /**
   * Enables integration with IVIS for enhanced simulation analytics and visualization.
   * When enabled, users will have access to advanced simulation insights and visualizations.
   */
  ENABLE_IVIS_INTEGRATION: {
    defaultValue: false,
    description: `Enable integration with IVIS for enhanced simulation analytics and visualization.`,
  },

  /**
   * Enables the user profile page.
   * Disable to hide profile navigation and route access.
   */
  ENABLE_USER_PROFILE_PAGE: {
    defaultValue: false,
    description: `Enable the /profile route.`,
  },

  /**
   * Enables the updated/v2 scoring API integration.
   * Disable to change back to v1 of the scorecard_json usage.
   */
  ENABLE_SCORECARD_V2: {
    defaultValue: true,
    description: `Enable v2 of the score card API integration.`,
  },

  /**
   * Enables variable thresholds for scorecard v2, allowing dynamic scoring criteria.
   * When disabled, scorecard v2 will use fixed thresholds as defined in the config.
   * This flag is intended to allow testing and gradual rollout of variable threshold functionality.
   */
  ENABLE_SCORECARD_V2_VARIABLE_THRESHOLDS: {
    defaultValue: false,
    description: `Enable variable thresholds for scorecard v2.`,
  },

  /**
   * Enables client-side Application Insights integration for telemetry and monitoring.
   * When enabled, the app will send telemetry data to Application Insights
   * for performance monitoring and error tracking.
   */
  ENABLE_APP_INSIGHTS: {
    defaultValue: true,
    description: `Enable client-side Application Insights integration for telemetry and monitoring.`,
  },

  /**
   * Enables the mic status indicator overlay on the voice live simulation.
   * When disabled, the microphone icon overlay is hidden from the avatar view.
   */
  ENABLE_MIC_STATUS_INDICATOR: {
    defaultValue: false,
    description: `Enable the mic status indicator overlay on the voice live simulation.`,
  },

  /**
   * Enables API health checks on pathname changes to monitor backend status and
   * show outage alerts. When enabled, the app will perform health checks on 
   * route changes and display alerts if the backend is experiencing issues.
   */
  ENABLE_HEALTH_CHECKS: {
    defaultValue: true,
    description: `Enable API health checks on pathname changes to monitor backend status and show outage alerts.`,
  },

  /**
   * Enables the input/output audio visualizer in the Voice Live simulation's
   * audio-only displays (both the degraded fallback and the normal audio-only
   * toggle). When disabled, the existing AudioSvg waveform is shown instead.
   */
  ENABLE_AUDIO_IO_VISUALIZER: {
    defaultValue: true,
    description: `Enable the input/output audio visualizer in the Voice Live audio-only displays.`,
  },
} as const;

/**
 * Type representing the keys of the feature flags defined in FEATURE_FLAG_DEFINITIONS.
 * Use this type anywhere a specific flag name is required.
 */
export type FeatureFlagKey = keyof typeof FEATURE_FLAG_DEFINITIONS;

/**
 * Type representing the resolved feature flags with their boolean values.
 * This is the shape of FEATURE_FLAGS and any resolved map of booleans.
 */
export type FeatureFlagMap = Record<FeatureFlagKey, boolean>;

/**
 * Parse NEXT_PUBLIC_FEATURE_FLAGS into a partial override map.
 *
 * Supported formats
 * - JSON object string: `{"FLAG":true}`
 * - Comma list: `FLAG_A,FLAG_B,!FLAG_C,FLAG_D=false`
 *
 * Behavior
 * - Ignores unknown flags.
 * - Ignores invalid values.
 */
const parseEnvFlagOverrides = (value: string | undefined): Partial<FeatureFlagMap> => {
  // Return empty overrides if the env variable is not set or empty
  if (!value) return {};

  try {
    // First attempt to parse as JSON for structured overrides
    const parsed = JSON.parse(value) as Record<string, unknown>;

    // Validate and filter parsed object to only include known flags with boolean values
    if (parsed && typeof parsed === 'object') {
      return Object.keys(parsed).reduce<Partial<FeatureFlagMap>>((acc, key) => {
        // Only include keys that are defined in FEATURE_FLAG_DEFINITIONS and have boolean values
        if (key in FEATURE_FLAG_DEFINITIONS && typeof parsed[key] === 'boolean') {
          acc[key as FeatureFlagKey] = parsed[key] as boolean;
        }

        return acc;
      }, {});
    }
  } catch {
    // Fall back to comma-list parsing
  }

  // Parse comma-separated list of flags for simple overrides
  return value.split(',').reduce<Partial<FeatureFlagMap>>((acc, rawToken) => {
    // Trim whitespace and skip empty tokens
    const token = rawToken.trim();
    if (!token) return acc;

    const isNegated = token.startsWith('!') || token.startsWith('-');
    const normalized = isNegated ? token.slice(1) : token;
    const [key, rawValue] = normalized.split('=');

    // Only process keys that are defined in FEATURE_FLAG_DEFINITIONS
    if (!key || !(key in FEATURE_FLAG_DEFINITIONS)) return acc;

    // If a value is explicitly provided, parse it as boolean
    if (rawValue !== undefined) {
      const valueLower = rawValue.trim().toLowerCase();
      acc[key as FeatureFlagKey] = valueLower === 'true';
      return acc;
    }

    // If no explicit value, use the presence of the flag to determine true/false
    acc[key as FeatureFlagKey] = !isNegated;
    return acc;
  }, {});
};

/**
 * Parsed runtime overrides sourced from NEXT_PUBLIC_FEATURE_FLAGS.
 * Empty when no env overrides are provided.
 */
const FEATURE_FLAG_OVERRIDES = parseEnvFlagOverrides(process.env.NEXT_PUBLIC_FEATURE_FLAGS);

/**
 * Resolved feature flag values for runtime checks.
 * Defaults are defined in FEATURE_FLAG_DEFINITIONS and optionally overridden
 * by NEXT_PUBLIC_FEATURE_FLAGS.
 */
export const FEATURE_FLAGS = (Object.keys(FEATURE_FLAG_DEFINITIONS) as FeatureFlagKey[]).reduce(
  (acc, key) => {
    const definition = FEATURE_FLAG_DEFINITIONS[key];
    acc[key] = FEATURE_FLAG_OVERRIDES[key] ?? definition.defaultValue;
    return acc;
  },
  {} as FeatureFlagMap
);

/**
 * Helper for feature-flag checks.
 * - Accepts a single flag or an array of flags.
 * - Returns true only if all provided flags are enabled.
 */
export const isFeatureEnabled = (flags: FeatureFlagKey | FeatureFlagKey[]) => {
  // Normalize to array for consistent processing
  if (Array.isArray(flags)) {
    return flags.every((flag) => FEATURE_FLAGS[flag]);
  }

  // Check a single flag
  return FEATURE_FLAGS[flags];
};
